import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CustomStep } from "@src/domains/Dashboard/aiSection/components/steps/CustomStep";
import { useQueryClient } from "@tanstack/react-query";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useGetOrg } from "../../../api/organizations/get-organization";
import { aiKeys } from "../../../api/queryKeys";
import { useIsAdmin } from "../../../api/user-auths/is-admin";
import { useWebsocket } from "../../../hooks/websocket";
import {
  dispatchExperienceAnalysis,
  experienceAnalysis,
} from "../../../store/slices/ai";
import { selectedExperience } from "../../../store/slices/experiences";

import { ChoosePreviousThread } from "./components/choosePreviousThread/ChoosePreviousThread";
import { AnalyzingDataStep } from "./components/steps/AnalyzingDataStep";
import { GatheringDataStep } from "./components/steps/GatheringDataStep";
import { GetStartedStep } from "./components/steps/GetStartedStep";
import { ResultsStep } from "./components/steps/resultsStep/ResultsStep";

export const AiSection = () => {
  const org = useGetOrg();
  const { lastJsonMessage } = useWebsocket(org.data.org.id);

  const enableAi = useConfiguration<boolean>("enable_ai")?.value;
  const experience = useSelector(selectedExperience);
  const analysis = useSelector(experienceAnalysis);
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();

  const hasAccess = isAdmin || enableAi;

  useEffect(() => {
    return () => {
      dispatch(
        dispatchExperienceAnalysis({
          type: "reset",
        })
      );
    };
  }, []);

  useEffect(() => {
    if (experience) {
      dispatch(
        dispatchExperienceAnalysis({
          type: "setExperience",
          value: experience,
        })
      );
    }
  }, [analysis.step]);

  useEffect(() => {
    if (
      lastJsonMessage?.type === "experienceAnalysisProgress" &&
      lastJsonMessage?.experienceId === experience.id
    ) {
      if (lastJsonMessage.action === "pendingGatheringData") {
        dispatch(
          dispatchExperienceAnalysis({
            type: "setDataProgress",
            value: lastJsonMessage.dataProgress,
          })
        );
      } else if (lastJsonMessage.action === "pendingAnalyzingData") {
        dispatch(
          dispatchExperienceAnalysis({
            type: "setAnalyzingData",
            value: {
              threadId: lastJsonMessage.threadId,
              createdAt: lastJsonMessage.createdAt,
            },
          })
        );
      } else if (
        lastJsonMessage.action === "resultsDelta" &&
        lastJsonMessage.threadId === analysis.threadId
      ) {
        dispatch(
          dispatchExperienceAnalysis({
            type: "resultsDelta",
            value: {
              messageId: lastJsonMessage.messageId,
              delta: lastJsonMessage.delta,
              role: lastJsonMessage.role,
              messageType: lastJsonMessage.messageType,
            },
          })
        );
        if (analysis.autoScroll) {
          const msgPanel = document.getElementById("msg-panel");
          if (msgPanel) {
            msgPanel.scrollTop = msgPanel.scrollHeight;
          }
        }
      } else if (
        lastJsonMessage.action === "resultsDone" &&
        lastJsonMessage.threadId === analysis.threadId
      ) {
        void queryClient.invalidateQueries({
          queryKey: aiKeys.one(
            org.data.org.id,
            `experience-analysis-history-${analysis!.experience!.id}`
          ),
        });
        dispatch(
          dispatchExperienceAnalysis({
            type: "resultsDone",
          })
        );
      } else if (lastJsonMessage.action === "custom") {
        dispatch(
          dispatchExperienceAnalysis({
            type: "custom",
            message: lastJsonMessage.message,
          })
        );
      }
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    if (analysis.autoScroll) {
      const msgPanel = document.getElementById("msg-panel");
      if (msgPanel) {
        msgPanel.scrollTop = msgPanel.scrollHeight;
      }
    }
  }, [analysis.messages.length]);

  if (!hasAccess || analysis.isHidden) {
    return null;
  }

  if (analysis.viewPreviousThreads) {
    return <ChoosePreviousThread />;
  }

  if (analysis.step === "resultsDone" || analysis.step === "resultsDelta") {
    return <ResultsStep />;
  }

  if (analysis.step === "start") {
    return <GetStartedStep />;
  }

  if (analysis.step === "pendingGatheringData") {
    return <GatheringDataStep />;
  }

  if (analysis.step === "pendingAnalyzingData") {
    return <AnalyzingDataStep />;
  }

  if (analysis.step === "custom") {
    return <CustomStep />;
  }

  return null;
};
